import axios from '../../utils/axios';
import { API_URL, stateArray } from '../../constants';
import { Permissions } from '../../reducers/types';

export const DRAFT_KEY = 'createSubmissionOnBehalfDraft';

export interface Draft {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  brand: string;
  model: string;
  year: string;
  description: string;
  serialNumber: string;
  imageDriveSideUrl: string;
  imageNonDriveSideUrl: string;
}

export type PhotoField = 'imageDriveSideUrl' | 'imageNonDriveSideUrl';

export const EMPTY_DRAFT: Draft = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  brand: '',
  model: '',
  year: '',
  description: '',
  serialNumber: '',
  imageDriveSideUrl: '',
  imageNonDriveSideUrl: '',
};

export const EMAIL_PATTERN = /\S+@\S+\.\S+/;
export const ZIP_PATTERN = /^\d{5}$/;
export const MIN_YEAR = 2010;

/** A four-digit model year from MIN_YEAR up to two years ahead (bikes ship ahead of the calendar). */
export const isValidYear = (year: string): boolean => {
  const value = Number(year);
  return /^\d{4}$/.test(year) && value >= MIN_YEAR && value <= new Date().getFullYear() + 2;
};

const PHOTO_PREP_TIMEOUT_MS = 30000;
const PHOTO_READ_ERROR = 'Could not read that photo. Try another one.';
export const MAX_PHOTO_SIDE = 1600;

/**
 * What is still missing before the form can be submitted, in the order staff fill it in.
 * Description and serial number are optional and never listed.
 */
export const missingFields = (draft: Draft): string[] => {
  const missing: string[] = [];
  if (!draft.firstName.trim()) missing.push('first name');
  if (!draft.lastName.trim()) missing.push('last name');
  if (!EMAIL_PATTERN.test(draft.email)) missing.push('a valid email');
  if (!draft.phone.trim()) missing.push('phone');
  if (!draft.address.trim()) missing.push('address');
  if (!draft.city.trim()) missing.push('city');
  if (!stateArray.includes(draft.state)) missing.push('state');
  if (!ZIP_PATTERN.test(draft.zip)) missing.push('a 5-digit zip');
  if (!isValidYear(draft.year || '')) missing.push('a valid year');
  if (!draft.brand.trim()) missing.push('brand');
  if (!draft.model.trim()) missing.push('model');
  if (!draft.imageDriveSideUrl) missing.push('drive-side photo');
  if (!draft.imageNonDriveSideUrl) missing.push('non-drive-side photo');
  return missing;
};

/** True when the draft holds anything staff entered, including an uploaded photo. */
export const hasDraftContent = (draft: Draft): boolean =>
  (Object.keys(EMPTY_DRAFT) as Array<keyof Draft>).some(field => !!draft[field]);

export const isDraftComplete = (draft: Draft): boolean => missingFields(draft).length === 0;

export const canCreateSubmissionOnBehalf = (): boolean => {
  try {
    const permissions: number[] = JSON.parse(localStorage.getItem('userPermissions') || '[]');
    return permissions.includes(Permissions.CREATE_SUBMISSION_ON_BEHALF);
  } catch {
    return false;
  }
};

/** Rejects when the promise does not settle in time; the image loader behind `compress` never rejects. */
export const withTimeout = <T>(promise: Promise<T>, ms: number, message: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

/** Scales a size down so its longest side is at most `maxSide`, keeping the aspect ratio. */
export const fitWithin = (width: number, height: number, maxSide: number) => {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const loadImage = (file: Blob) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(PHOTO_READ_ERROR));
    };
    image.src = objectUrl;
  });

/**
 * Shrinks a photo to a JPEG no larger than `maxSide` on its longest side. Every failure rejects, so
 * a phone that cannot decode a large photo or allocate the canvas reports it instead of throwing.
 */
export const resizeToJpeg = async (
  file: Blob,
  maxSide = MAX_PHOTO_SIDE,
  quality = 0.9,
): Promise<Blob> => {
  const image = await loadImage(file);
  const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight, maxSide);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error(PHOTO_READ_ERROR);
  }
  context.drawImage(image, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error(PHOTO_READ_ERROR))),
      'image/jpeg',
      quality,
    ),
  );
};

const putWithProgress = (signedUrl: string, blob: Blob, onProgress: (percent: number) => void) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Photo upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Photo upload failed. Check your connection and retry.'));
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', 'image/jpeg');
    xhr.send(blob);
  });

/** Shrinks the photo, uploads it through a signed S3 url, and returns the public url. */
export const uploadPhoto = async (
  file: File,
  onProgress: (percent: number) => void,
): Promise<string> => {
  // The axios wrapper only resolves with nothing when it is reloading the page for an expired session
  const signed = await axios.get(`${API_URL}/tradeup/signedUrl`);
  if (!signed) {
    throw new Error('Your session expired. Sign in again.');
  }
  const compressed = await withTimeout(resizeToJpeg(file), PHOTO_PREP_TIMEOUT_MS, PHOTO_READ_ERROR);
  await putWithProgress(signed.data.signedRequest, compressed, onProgress);
  return signed.data.url;
};

export const submitDraft = async (draft: Draft): Promise<number | undefined> => {
  const response = await axios.post(`${API_URL}/tradeup/submissionOnBehalf`, {
    ...draft,
    year: Number(draft.year),
    description: draft.description.trim() || undefined,
    serialNumber: draft.serialNumber.trim() || undefined,
  });
  return response ? response.data.submissionId : undefined;
};
